import type { JSX } from 'react';
import IconRightArrow from '../icons/IconRightArrow';
import { formatCurrency } from '../helpers/formatters';
import type { ShowFeesItem } from '../../openapi/model/showFeesItem';
import { ShowIsBreakingOutFees } from '../../openapi/model/showIsBreakingOutFees';
import { ShowPriceListingMode } from '../../openapi/model/showPriceListingMode';
import { useLang } from '../hooks/useLanguage';
import { useCart } from '../hooks/useCart';
import { calculateProcessingFee } from '../helpers/calculateProcessingFee';
import Tooltip from './Tooltip';
import type { IVerificationState } from '../hooks/useVerificationPreflight';
import PriceLoader from './PriceLoader';

export interface NextToEventProps {
    nextStep?: () => void;
    label: string;
    disabled?: boolean;
    hasNextIcon?: boolean;
    showFees: ShowFeesItem[];
    isBreakingOutFees: ShowIsBreakingOutFees;
    priceListingMode?: ShowPriceListingMode;
    cardProcessingFee?: { percentage?: number; usd?: number } | null;
    totalNote?: React.ReactNode;
    verification?: IVerificationState;
    /** Human-readable reasons the CTA is disabled — surfaced so guests
     *  know what to fix instead of guessing at the greyed-out button. */
    disabledReasons?: string[];
}

export default function NextToEvent({
    nextStep,
    label,
    disabled = false,
    hasNextIcon = true,
    showFees,
    isBreakingOutFees,
    priceListingMode = ShowPriceListingMode.gross,
    cardProcessingFee,
    totalNote,
    verification,
    disabledReasons,
}: Readonly<NextToEventProps>): JSX.Element {
    const {
        getFinalPrice,
        getFinalPriceWithDiscount,
        getFinalPriceAfterAdjustment,
        getTaxesAndFeesAggregated,
        appliedCoupon,
        totalPrice: itemsNetTotal,
        verificationDeltaCents,
        isPricingLoading,
    } = useCart();
    const { t } = useLang();
    const subtotal = getFinalPrice(showFees);
    const totalBeforeAdjustment = getFinalPriceWithDiscount(showFees);
    // Net inline discount = user coupon discount minus the verification
    // script delta (script discount adds, script surcharge subtracts).
    const discount = subtotal - totalBeforeAdjustment - verificationDeltaCents;
    const total = getFinalPriceAfterAdjustment(showFees);
    const isVerificationBlocking = verification?.status === 'error';
    const isVerificationLoading = verification?.status === 'loading';

    // Net listing always discloses the breakdown (subtotal-net + booking fee)
    // — that's what makes the listed price meaningful to the guest. Gross
    // listings keep the saved isBreakingOutFees gating.
    const showBreakdown =
        (priceListingMode === ShowPriceListingMode.net ||
            isBreakingOutFees !== ShowIsBreakingOutFees.NUMBER_0) &&
        itemsNetTotal > 0;

    let totalTaxesAndFees = 0;
    let estimatedProcessingFee = 0;
    if (showBreakdown) {
        const { totalFeeAmount, totalTaxAmount } = getTaxesAndFeesAggregated(showFees);
        totalTaxesAndFees = totalFeeAmount + totalTaxAmount;
        if (cardProcessingFee) {
            estimatedProcessingFee = calculateProcessingFee(total, {
                percentage: Number(cardProcessingFee.percentage) || 0,
                usd: Number(cardProcessingFee.usd) || 0,
            });
        }
    }

    return (
        <div className="fireknife-next-to-event">
            {showBreakdown && (
                <div className="fireknife-next-to-event__breakdown">
                    <div className="fireknife-next-to-event__breakdown-row">
                        <div className="fireknife-next-to-event__breakdown-label">
                            {t('widgetBookingSummary.subtotalNet')}
                        </div>
                        <div className="fireknife-next-to-event__breakdown-value">
                            {isPricingLoading ? (
                                <PriceLoader />
                            ) : (
                                formatCurrency(itemsNetTotal / 100, t('widgetLocale'))
                            )}
                        </div>
                    </div>
                    {totalTaxesAndFees > 0 && (
                        <div className="fireknife-next-to-event__breakdown-row">
                            <div className="fireknife-next-to-event__breakdown-label">
                                {t('widgetBookingSummary.bookingFee')}
                                <Tooltip
                                    ariaLabel={t('widgetBookingSummary.bookingFeeInfo')}
                                    className="fireknife-next-to-event-fee-tooltip"
                                >
                                    {t('widgetBookingSummary.bookingFeeInfoText')}
                                </Tooltip>
                            </div>
                            <div className="fireknife-next-to-event__breakdown-value">
                                {isPricingLoading ? (
                                    <PriceLoader />
                                ) : (
                                    formatCurrency(totalTaxesAndFees / 100, t('widgetLocale'))
                                )}
                            </div>
                        </div>
                    )}
                    {estimatedProcessingFee > 0 && (
                        <div className="fireknife-next-to-event__breakdown-row">
                            <div className="fireknife-next-to-event__breakdown-label">
                                {t('widgetReservationInfo.paymentDetails.processingFee')}
                            </div>
                            <div className="fireknife-next-to-event__breakdown-value">
                                {isPricingLoading ? (
                                    <PriceLoader />
                                ) : (
                                    formatCurrency(
                                        estimatedProcessingFee / 100,
                                        t('widgetLocale'),
                                    )
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
            {!isPricingLoading && discount !== 0 && (
                <div className="fireknife-next-to-event__discount">
                    <div className="fireknife-next-to-event__discount-label">
                        {t('coupons.discount')}
                        {appliedCoupon ? ` (${appliedCoupon.code})` : ''}
                    </div>
                    <div className="fireknife-next-to-event__discount-desc">
                        {discount > 0 ? '-' : '+'}
                        {formatCurrency(Math.abs(discount) / 100, t('widgetLocale'))}
                    </div>
                </div>
            )}
            {isVerificationLoading && (
                <div className="fireknife-next-to-event__discount">
                    <div className="fireknife-next-to-event__discount-label">
                        {t('errors.verifying')}
                    </div>
                </div>
            )}
            {disabledReasons && disabledReasons.length > 0 ? (
                <div role="alert" className="fireknife-verification-error">
                    {disabledReasons.map((reason, idx) => (
                        <div key={idx}>{reason}</div>
                    ))}
                </div>
            ) : (
                isVerificationBlocking &&
                verification?.errorMessage && (
                    <div role="alert" className="fireknife-verification-error">
                        {verification.errorMessage}
                    </div>
                )
            )}
            <div className="fireknife-next-to-event__total">
                <div className="fireknife-next-to-event__total-label">
                    {t('widgetBookingSummary.total')}:
                </div>
                <div className="fireknife-next-to-event__total-desc">
                    {isPricingLoading ? (
                        <PriceLoader />
                    ) : (
                        formatCurrency((total + estimatedProcessingFee) / 100, t('widgetLocale'))
                    )}
                </div>
            </div>
            {totalNote}

            <button
                className={`fireknife-next-to-event__btn fireknife-btn fireknife-btn--primary ${
                    disabled || isVerificationBlocking || isVerificationLoading || isPricingLoading
                        ? 'fireknife-next-to-event__btn--disabled'
                        : ''
                }`}
                type="button"
                onClick={nextStep ?? (() => {})}
                aria-label={label}
                disabled={disabled || isVerificationBlocking || isVerificationLoading || isPricingLoading}
            >
                <span className="fireknife-next-to-event__btn-text">{label}</span>
                {hasNextIcon && <IconRightArrow className="fireknife-next-to-event__btn-icon" />}
            </button>
        </div>
    );
}
